/* eslint-disable react/prop-types */
import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { GET_COURSE_DETAILS } from "graphql/query/admin";
import { DELETE_COURSE_VIDEO } from "graphql/mutation/admin";
import ConfirmationModal from "Components/Modal/ConfirmationModal";
import CourseFormModal from "../Components/CourseForm/CourseFormModal";
import AddVideoModal from "./AddVideoModal";

const InfoRow = ({ label, value }) => (
  <Grid container style={{ marginBottom: 10 }}>
    <Grid item xs={4} sm={3}>
      <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555" }}>{label}</Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography style={{ fontSize: 14 }}>{value || "N/A"}</Typography>
    </Grid>
  </Grid>
);

const ViewCourse = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const courseId = state?.id;
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteCourseVideo] = useMutation(DELETE_COURSE_VIDEO);

  const { data, loading, refetch } = useQuery(GET_COURSE_DETAILS, {
    variables: { courseId },
    skip: !courseId,
    fetchPolicy: "network-only",
  });

  const course = data?.course;

  const handleDeleteVideo = async () => {
    if (!videoToDelete?.id) return;
    try {
      setDeleteLoading(true);
      const { data: res } = await deleteCourseVideo({
        variables: { input: { courseId, videoId: videoToDelete.id } },
      });
      const { status, message } = res.deleteCourseVideo;
      if (status === "success") {
        toast.success(message || "Video deleted successfully");
        setVideoToDelete(null);
        refetch();
      } else {
        toast.error(message || "Failed to delete video");
      }
      setDeleteLoading(false);
    } catch (err) {
      toast.error(err.message || "Failed to delete video");
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!course) {
    return (
      <Container maxWidth={false} disableGutters style={{ margin: "0 2%" }}>
        <Typography>No course data found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters style={{ margin: "0 2%", minHeight: "70vh" }}>
      {/* Breadcrumb */}
      <Typography style={{ fontWeight: "bold", display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <Link to="/admin/course" style={{ color: "black", textDecoration: "none", marginRight: 4 }}>Course</Link>
        &gt;
        <span style={{ fontWeight: "bold", marginLeft: 4 }}>Course Detail</span>
      </Typography>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
        variant="outlined"
        size="small"
      >
        Back
      </Button>

      {/* Course Info */}
      <Box style={{ background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
        <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {course.name || "Course Detail"}
          </Typography>
          <Chip
            label={course.isActive ? "Active" : "Inactive"}
            size="small"
            style={{
              background: course.isActive ? "#e8f5e9" : "#ffebee",
              color: course.isActive ? "#2e7d32" : "#c62828",
              fontWeight: 600,
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditOpen(true)}
            style={{ marginLeft: "auto" }}
          >
            Edit Course
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={course.image?.url ? 8 : 12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoRow label="Name" value={course.name} />
                <InfoRow label="Type" value={course.type} />
                <InfoRow label="Tag" value={course.tag} />
                <InfoRow
                  label="Price"
                  value={
                    course.price !== null && course.price !== undefined
                      ? course.price
                      : null
                  }
                />
                <InfoRow label="Owner" value={course.owner} />
                <InfoRow label="Created By" value={course.createdBy?.fullName} />
                <InfoRow label="Updated By" value={course.updatedBy?.fullName} />
                <InfoRow
                  label="Created At"
                  value={course.createdAt ? moment(course.createdAt).format("DD/MM/YYYY HH:mm") : null}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
                  Description
                </Typography>
                <Typography style={{ fontSize: 14 }}>
                  {course.description || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {course.image?.url && (
            <Grid item xs={12} md={4}>
              <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 8 }}>
                Course Image
              </Typography>
              <img
                src={course.image.url}
                alt={course.image.name || "course-image"}
                style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }}
              />
            </Grid>
          )}
        </Grid>

        {/* Videos */}
        <Box style={{ marginTop: 24 }}>
          <Divider style={{ marginBottom: 16 }} />
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <Typography style={{ fontWeight: 700 }}>
              Videos ({course.videos?.length || 0})
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddVideoOpen(true)}
            >
              Add Video
            </Button>
          </Box>
          {Array.isArray(course.videos) && course.videos.length > 0 ? (
            <Grid container spacing={2}>
              {course.videos.map((video, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item xs={12} sm={6} md={4} key={video.id || i}>
                  <Box style={{ background: "#f9f9f9", borderRadius: 8, padding: 12, position: "relative" }}>
                    {video.id && (
                      <IconButton
                        size="small"
                        onClick={() => setVideoToDelete(video)}
                        title="Delete video"
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          background: "rgba(255,255,255,0.85)",
                          zIndex: 1,
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" style={{ color: "#c62828" }} />
                      </IconButton>
                    )}
                    {video.mediaUrl ? (
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        src={video.mediaUrl}
                        poster={video.thumbnailUrl || undefined}
                        style={{
                          width: "100%",
                          aspectRatio: "16 / 9",
                          display: "block",
                          borderRadius: 6,
                          marginBottom: 8,
                          background: "#000",
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      video.thumbnailUrl && (
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title || `video-${i}`}
                          style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 6, marginBottom: 8 }}
                        />
                      )
                    )}
                    <Typography style={{ fontWeight: 600, fontSize: 13 }}>
                      {video.title || `Video ${i + 1}`}
                    </Typography>
                    {video.duration && (
                      <Typography style={{ fontSize: 12, color: "#888" }}>
                        Duration: {video.duration}s
                      </Typography>
                    )}
                    {video.description && (
                      <Typography style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                        {video.description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography style={{ fontSize: 14, color: "#888" }}>
              No videos added yet.
            </Typography>
          )}
        </Box>
      </Box>

      {addVideoOpen && (
        <AddVideoModal
          open={addVideoOpen}
          handleClose={() => setAddVideoOpen(false)}
          courseId={courseId}
          existingTitles={(course.videos || []).map((v) => v.title)}
          handleOnSuccess={() => refetch()}
        />
      )}

      {editOpen && (
        <CourseFormModal
          open={editOpen}
          course={course}
          handleClose={() => setEditOpen(false)}
          handleOnSuccess={() => refetch()}
        />
      )}

      {videoToDelete && (
        <ConfirmationModal
          open={Boolean(videoToDelete)}
          handleClose={() => {
            if (!deleteLoading) setVideoToDelete(null);
          }}
          heading="Delete Video"
          subtitle={`Are you sure you want to delete "${videoToDelete.title}"?`}
          button1="Cancel"
          button2="Delete"
          onButton1Click={() => setVideoToDelete(null)}
          onButton2Click={handleDeleteVideo}
          isLoading={deleteLoading}
        />
      )}
    </Container>
  );
};

export default ViewCourse;
